import { zodResolver } from '@hookform/resolvers/zod';
import { eggDetailSchema, nestInputSchema, nestSchema, okSchema, type Nest } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileUp, Loader2, Pencil, Plus, Trash2, Egg as EggIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { allEggsQuery, nestsQuery } from '@/lib/admin-queries';
import { applyServerErrors, errorMessage } from '@/lib/forms';

export const Route = createFileRoute('/_app/admin/nests/')({ component: NestsPage });

function NestsPage() {
  const nests = useQuery(nestsQuery);
  const eggs = useQuery(allEggsQuery);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Nest | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/application/nests/${id}`, { method: 'DELETE', schema: okSchema }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'nests'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <>
      <PageHeader
        title="Nests & eggs"
        icon={EggIcon}
        description="Eggs define how a kind of server is installed and run. Pterodactyl eggs (PTDL_v1/v2) import as-is."
        actions={
          <>
            <Button variant="outline" onClick={() => setEditing('new')}>
              <Plus /> New nest
            </Button>
            <Button onClick={() => setImporting(true)}>
              <FileUp /> Import egg
            </Button>
          </>
        }
      />
      <QueryState query={nests}>
        {(list) => (
          <div className="grid gap-6">
            {list.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No nests yet. Run <code>hearth seed</code> to add the bundled eggs.
              </p>
            )}
            {list.map((nest) => (
              <Card key={nest.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                  <div className="grid gap-1.5">
                    <CardTitle>{nest.name}</CardTitle>
                    {nest.description && <CardDescription>{nest.description}</CardDescription>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${nest.name}`}
                      onClick={() => setEditing(nest)}
                    >
                      <Pencil />
                    </Button>
                    <Confirm
                      title={`Delete ${nest.name}?`}
                      description="Only empty nests can be deleted."
                      action="Delete"
                      destructive
                      onConfirm={() => remove.mutate(nest.id)}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${nest.name}`}
                        disabled={nest.eggCount > 0}
                      >
                        <Trash2 />
                      </Button>
                    </Confirm>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y">
                    {(eggs.data ?? [])
                      .filter((e) => e.nestId === nest.id)
                      .map((egg) => (
                        <li key={egg.id} className="flex items-center gap-3 py-2">
                          <Link
                            to="/admin/eggs/$eggId"
                            params={{ eggId: String(egg.id) }}
                            className="font-medium hover:underline"
                          >
                            {egg.name}
                          </Link>
                          <span className="text-muted-foreground min-w-0 flex-1 truncate text-xs">
                            {egg.description}
                          </span>
                          <Badge variant="outline" className="shrink-0">
                            {Object.keys(egg.dockerImages).length} image
                            {Object.keys(egg.dockerImages).length === 1 ? '' : 's'}
                          </Badge>
                        </li>
                      ))}
                    {nest.eggCount === 0 && (
                      <li className="text-muted-foreground py-2 text-sm">No eggs.</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </QueryState>
      {editing && (
        <NestDialog nest={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {importing && <ImportDialog nests={nests.data ?? []} onClose={() => setImporting(false)} />}
    </>
  );
}

type NestValues = z.input<typeof nestInputSchema>;

function NestDialog({ nest, onClose }: { nest: Nest | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const form = useForm<NestValues>({
    resolver: zodResolver(nestInputSchema),
    defaultValues: { name: nest?.name ?? '', description: nest?.description ?? '' },
  });
  const save = useMutation({
    mutationFn: async (body: NestValues) => {
      if (nest)
        await apiRequest(`/api/application/nests/${nest.id}`, {
          method: 'PATCH',
          body,
          schema: okSchema,
        });
      else await apiRequest('/api/application/nests', { method: 'POST', body, schema: nestSchema });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'nests'] });
      onClose();
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{nest ? 'Edit nest' : 'New nest'}</DialogTitle>
        </DialogHeader>
        <form
          id="nest-form"
          className="grid gap-4"
          noValidate
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
        >
          <FormField label="Name" error={form.formState.errors.name?.message}>
            <Input autoFocus {...form.register('name')} />
          </FormField>
          <FormField label="Description" error={form.formState.errors.description?.message}>
            <Textarea rows={2} {...form.register('description')} />
          </FormField>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="nest-form" disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ nests, onClose }: { nests: Nest[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [nestId, setNestId] = useState<string>(nests[0] ? String(nests[0].id) : '');
  const [error, setError] = useState<string | null>(null);
  const importEgg = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error('Choose an egg JSON file.');
      if (file.size > 2 * 1024 * 1024)
        throw new Error('That file is larger than 2 MiB; eggs are much smaller.');
      let egg: unknown;
      try {
        egg = JSON.parse(await file.text());
      } catch {
        throw new Error(`${file.name} is not valid JSON.`);
      }
      return apiRequest('/api/application/eggs/import', {
        method: 'POST',
        body: { nestId: Number(nestId), egg },
        schema: eggDetailSchema,
      });
    },
    onSuccess: async (egg) => {
      await queryClient.invalidateQueries({ queryKey: ['admin'] });
      toast.success(`Imported ${egg.name}.`);
      onClose();
      await navigate({ to: '/admin/eggs/$eggId', params: { eggId: String(egg.id) } });
    },
    onError: (e) => setError(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import an egg</DialogTitle>
          <DialogDescription>
            Use any Pterodactyl egg file, e.g. from{' '}
            <a
              className="underline"
              href="https://github.com/pelican-eggs"
              target="_blank"
              rel="noreferrer noopener"
            >
              pelican-eggs
            </a>
            .
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <FormField label="Egg file (.json)">
            <Input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={() => setError(null)}
            />
          </FormField>
          <FormField label="Nest">
            <Select value={nestId} onValueChange={setNestId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Pick a nest" />
              </SelectTrigger>
              <SelectContent>
                {nests.map((n) => (
                  <SelectItem key={n.id} value={String(n.id)}>
                    {n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          {error && (
            <p className="text-destructive text-sm whitespace-pre-wrap" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => importEgg.mutate()} disabled={importEgg.isPending || !nestId}>
            {importEgg.isPending && <Loader2 className="animate-spin" />} Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
